import { useState, useEffect, useRef } from 'react'
import { ArrowDownUp, Info } from 'lucide-react'
import { TokenPill } from '@/components/TokenPill'
import { WalletChip } from '@/components/WalletChip'
import { FeeSection } from '@/components/FeeSection'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useUI } from '@/hooks/useUI'
import { useFitFont } from '@/hooks/useFitFont'
import { FONT_HEADING, FONT_BODY, BRAND_GRADIENT } from '@/config/theme'
import { formatTokenAmount, floorToDecimals } from '@/utils/format'
import { CHAIN_REGISTRY } from '@/config/chains'
import { TOKEN_PICKER_LIST } from '@/config/tokens'
import { isWalletTypeLabelEnabled } from '@/config/flags'
import type { Chain, FeeEstimate } from '@/types'

interface SwapCardProps {
  sourceChain: Chain | null
  destinationChain: Chain | null
  /**
   * #614 — whether the selected route is an ATOMIC same-chain swap (deBridge / Jupiter). Threaded from
   * BridgePage's single predicate; a same-chain `near_1click` deposit flow is NOT atomic and gets the
   * cross-chain delivery estimate. ABSENT ⇒ false (fail closed).
   */
  isAtomicSameChain?: boolean
  sourceAddress: string | null
  destinationAddress: string | null
  isSourceIncompatible: boolean
  isDestIncompatible: boolean
  isRecipientMissing: boolean
  amount: string
  onAmountChange: (amount: string) => void
  estimatedReceived: string
  estimatedReceivedUsd?: string | null
  /** DEX-only: USD value of the SEND amount (native source ≠ $1, so formatFiatAmount on the raw token is wrong). */
  sendAmountUsd?: string | null
  sourceBalance: string | null
  /**
   * #301 item 3 — the parent's `isBalancesLoading`, i.e. "a balance fetch is in flight".
   *
   * NEVER pass `isLoading` here. That is the TRANSFER state (it drives the CTA to "Processing..."
   * and disables every input); a balance refresh must not look like a transfer in progress.
   *
   * It widens the balance-row gate so the row is PRESENT with a `—` placeholder during the load
   * instead of appearing out of nowhere when the fetch lands. It deliberately does NOT gate Half/Max
   * — see the `disabled` prop on those buttons for why that would be unsafe.
   */
  isBalanceLoading?: boolean
  /**
   * GATE-4 fix — did the load that just cleared `isBalanceLoading` actually SUCCEED, as opposed to
   * completing with every leg failed? Optional and defaults to `true` so a caller that never passes
   * it gets the ORIGINAL latch behaviour unchanged (arms on any observed `true → false` transition
   * with `sourceBalance === null`, success or not).
   *
   * The parent (`BridgePage.tsx`, `fetchAllBalances`) already computes this exactly — it is the
   * `allLegsSucceeded` value that also gates smart-select — because `balanceApi` NEVER rejects: a
   * network error, non-OK response, or bad JSON all resolve `{success:false}`, so a completed
   * `isBalanceLoading` cycle and a completed-and-INFORMATIVE one are not the same thing. Without this
   * signal the below-latch cannot tell "this token genuinely has no balance" from "the fetch failed
   * and we still don't know" — both look identical from `sourceBalance === null` alone — and a single
   * failed first load would permanently suppress the pending row for that selection, even on retries
   * that later fail again.
   */
  lastBalanceLoadSucceeded?: boolean
  /**
   * #301a RE-ENTRY 3 — the live route table (`GET /api/dex/routes`) is still on its FIRST fetch.
   *
   * NEVER pass `dexRoutes.length === 0` here. That is also true for a table that arrived empty (or
   * partial, or failed), which would leave the chain control permanently unavailable instead of
   * transiently pending. The parent passes the query's own `isLoading`.
   *
   * While true, BOTH chain buttons are unavailable and show a loading affordance — the routing
   * evidence a network pick needs is not in yet, and a pick made in that window persists a chain
   * (and, on the source side, can persist a token) that nothing later recovers. Defaults to false so
   * a caller that never passes it keeps the pre-#301a always-enabled behaviour.
   */
  isRoutesLoading?: boolean
  /** The token amount that leaves the wallet ON TOP of the send, in SOURCE-TOKEN units, so
   *  "Max" = balance − reserve is actually sendable. 0/undefined → Max uses the full balance.
   *
   *  Legacy (#261 F7 flag OFF): set only for a DEX cross-chain STABLE source, where it is the route
   *  fee (opex) the router pulls on top of the input.
   *
   *  F7 flag ON: set only for a NATIVE source, where it is padded gas + the deBridge flat fixFee —
   *  both native, both paid on top of the give. A stable source then reserves NOTHING (its Max is the
   *  full balance). The parent owns which model applies; this component only subtracts. */
  maxReserve?: number
  /**
   * #261 F7 — `isNativeMaxGasReserveEnabled()`, threaded from the parent. Gates EVERY F7 behaviour in
   * this component: the tracked Max state, the cold-start re-snap, the exact-balance blur snap, and
   * the reserve note.
   *
   * THIS PROP EXISTS BECAUSE `maxReserve > 0` IS NOT A PROXY FOR THE FLAG. Under the flag OFF the
   * parent still passes the LEGACY stable-source `opexUsd` reserve, which is non-zero for every
   * cross-chain route with a live quote — i.e. the ordinary production state. Gating the new
   * behaviours on `maxReserve > 0` therefore shipped them to production users with the flag unset:
   * typing your exact balance and tabbing away silently rewrote the field, with no note to explain it
   * (the note is native-only, and this path is stable-only). Caught in review; the prop is the fix.
   *
   * It also restores the convergence argument below. The flag-ON reserve is amount-INDEPENDENT; the
   * legacy `opexUsd` is `amountUsd − receiveUsd` and so is strictly increasing in the amount, which
   * makes the re-snap a genuine fixed-point iteration rather than a one-shot. Gating on the flag is
   * what makes "converges structurally" true rather than merely intended.
   */
  reserveAware?: boolean
  /**
   * #261 F7 — freeze the Max re-snap while a confirmation dialog is open. This card stays MOUNTED
   * behind the review modal, and the modal reads `amount` as a live prop, so an estimate or balance
   * refresh resolving mid-review would move the number the user is looking at — and `handleReviewConfirm`
   * executes whatever the amount is at CONFIRM time, not at open time. A confirmation dialog whose
   * amount can change after it is read is not a confirmation.
   */
  snapSuspended?: boolean
  /**
   * #261 F7 (GATE-4 F-A) — true when `maxReserve` reflects a REAL estimate, false when the estimate is
   * merely absent (blanked mid-refetch, or not yet fetched).
   *
   * The monotone guard below refuses upward revisions, which is what stops the refetch-blank
   * oscillation — the reserve drops to 0 during every refetch and would otherwise re-fill the FULL
   * balance. But "reserve is 0 because we have no estimate" and "reserve is genuinely lower now" are
   * different facts, and the guard alone cannot tell them apart. Conflating them PINNED a Max session
   * at a stale low: one glitched gas poll (the ETH oracle RPC fallback is ~6x) snapped the field down,
   * and when gas recovered the correct, higher max was rejected as an "upward" move — leaving the user
   * ~0.18 ETH below their real max with the note still calling it their max.
   *
   * With this flag the guard permits an upward revision ONLY when an estimate actually backs it. The
   * blank case stays rejected, so the oscillation fix is untouched; the recovery case is accepted, so
   * the field self-corrects instead of requiring the user to re-tap Max (which nothing told them to do).
   */
  reserveReady?: boolean
  /**
   * #261 F7 (GATE-4 pass-3 door B) — react-query's `dataUpdatedAt` for the estimate behind
   * `maxReserve`: the wall-clock at which it was FETCHED, not when it was served.
   *
   * The re-snap acts at most once per genuinely new estimate, tracked as a high-water mark. A
   * comparison against "the timestamp of the last snap" is NOT enough and was the trap here: with two
   * crossed cache entries the timestamps alternate `t1, t2, t1, t2…` and `t2 > t1` stays true forever,
   * so the cycle survives. Against a high-water mark, `t2` is accepted once and every later re-serve
   * of either entry is refused, which is what actually breaks it.
   *
   * `0` means no data (react-query's value when `data` is undefined), so the refetch-blank window is
   * rejected by the same test — the original F-1 oscillation is closed by freshness alone, with the
   * value-monotone guard as a second, independent line.
   */
  reserveUpdatedAt?: number
  destBalance?: string | null
  sourceToken?: string
  destToken?: string
  onSourceChainClick: () => void
  onDestChainClick: () => void
  onSourceTokenClick?: () => void
  onDestTokenClick?: () => void
  /**
   * #261 F3 — the SEND chip's brand icon: the `walletKind` of the wallet connected on the SOURCE
   * chain, resolved by the parent through the shared normaliser (`utils/walletLabel.ts`) so the key
   * is a `WALLET_LOGO_KEY` entry. `null`/absent → `WalletChip` keeps the generic glyph, which is also
   * what an unmapped connector must produce (never a wrong brand). Display only.
   */
  sourceWalletKind?: string | null
  /**
   * #261 F3 — the RECEIVE chip's brand icon. FUND-SAFETY GATED (ADDENDUM A4 / F3-M2): the parent
   * passes a kind ONLY when `recipientIsConnectedWallet`, and `null` for a pasted/third-party
   * recipient. Do NOT "simplify" this to reuse `sourceWalletKind` — the receive chip renders
   * `destinationAddress`, which can be someone else's address, and stamping the user's own wallet
   * logo on it recreates the false "this is mine" cue F3 exists to remove, in icon form.
   */
  destWalletKind?: string | null
  /**
   * Fixer pass, F-1 — is the RECEIVE chip's address the user's OWN connected wallet, as opposed to a
   * pasted / third-party recipient? Drives the receive `WalletChip`'s `isOwnWallet` (the accessible
   * name's ownership claim), which is UNFLAGGED (ADDENDUM A5) and therefore NOT gated on
   * `walletBrandOn` below — unlike `destWalletKind`, which only controls the (flagged) brand icon.
   * Defaults to `false`: an omitted prop must never cause a pasted recipient to be announced as the
   * user's own wallet.
   */
  recipientIsConnectedWallet?: boolean
  onSourceWalletClick: () => void
  onDestWalletClick: () => void
  onSwapDirection: () => void
  onSetRecipient: () => void
  onSend: () => void
  onConnectWallet?: () => void
  isLoading?: boolean
  /** Quote-fetch state (react-query `isFetching` for the DEX estimate). Drives the receive-field
   *  spinner (beta bug 4). DISTINCT from `isLoading`, which is the TRANSFER state (see note above). */
  isQuoteLoading?: boolean
  canSend?: boolean
  ctaLabel?: string
  feeEstimate?: FeeEstimate | null
  secondsRemaining?: number
  fiatSymbol?: string
  fiatRate?: number | null
  fiatStale?: boolean
  formatFiatAmount?: (usdcAmount: string | number) => string | null
  onReceiveAmountChange?: (value: string) => void
  receiveEditable?: boolean
  /** DEX-only: "Minimum received" in dest-token terms (e.g. native ETH), overrides the USDC default. */
  minReceivedAmount?: string | null
  minReceivedSymbol?: string
  /** DEX-only: full fee breakdown in dest-token terms; shown instead of the CCTP feeEstimate. */
  dexFee?: {
    totalFee: string
    parseFee: string
    affiliateBps: number
    minimumReceived: string | null
    currency: string
    warningLevel: 'green' | 'orange' | 'red'
    estimatedTimeMin?: number
    feesTotalUsd?: number
    fixFeeNative?: string
    fixFeeSymbol?: string
    fixFeeUsd?: number
    youPaySource?: string
    youPayUsd?: number
    sourceCurrency?: string
  } | null
}

export function SwapCard({
  sourceChain,
  destinationChain,
  isAtomicSameChain,
  sourceAddress,
  destinationAddress,
  isSourceIncompatible,
  isDestIncompatible,
  isRecipientMissing,
  amount,
  onAmountChange,
  estimatedReceived,
  estimatedReceivedUsd,
  sendAmountUsd,
  sourceBalance,
  isBalanceLoading = false,
  lastBalanceLoadSucceeded = true,
  isRoutesLoading = false,
  maxReserve = 0,
  reserveAware = false,
  snapSuspended = false,
  reserveReady = false,
  reserveUpdatedAt = 0,
  destBalance,
  sourceToken = 'USDC',
  destToken = 'USDC',
  onSourceChainClick,
  onDestChainClick,
  onSourceTokenClick,
  onDestTokenClick,
  sourceWalletKind,
  destWalletKind,
  recipientIsConnectedWallet = false,
  onSourceWalletClick,
  onDestWalletClick,
  onSwapDirection,
  onSetRecipient,
  onSend,
  onConnectWallet,
  isLoading = false,
  isQuoteLoading = false,
  canSend = false,
  ctaLabel,
  feeEstimate,
  secondsRemaining = 60,
  fiatSymbol = '$',
  fiatRate,
  fiatStale,
  formatFiatAmount,
  onReceiveAmountChange,
  receiveEditable = false,
  minReceivedAmount,
  minReceivedSymbol,
  dexFee,
}: SwapCardProps) {
  const c = useThemeColors()
  const UI = useUI()

  const [localAmount, setLocalAmount] = useState(amount)
  const lastDispatchedRef = useRef(localAmount)
  const activeInputRef = useRef<'send' | 'receive' | null>(null)

  // #301 item 3 (flicker fix) — the balance-pending row (below, `balance-pending`) must show only on
  // the FIRST load for the current selection, not on every background refresh. `isBalanceLoading` is
  // the PARENT's `isBalancesLoading`, which `fetchAllBalances` (BridgePage.tsx) re-raises on its 30s
  // timer regardless of whether anything changed. For any source token with NO entry in the balance
  // map on the current chain (e.g. USDT on a Robinhood-only source, or a chain whose leg dropped —
  // `services/api.ts:346`), `sourceBalance` stays permanently null while `isBalanceLoading` cycles
  // true→false→true forever — the row would appear with `—` and greyed Half/Max for the fetch
  // duration, then vanish, twice a minute, shifting the amount field's neighbours on a narrow mobile
  // webview. Before the pending row existed the row was simply absent and stable; this restores that
  // stability for every load AFTER the first.
  //
  // Mechanism: once a load COMPLETES (`!isBalanceLoading`) for the current selection and still leaves
  // `sourceBalance` null, the absence is KNOWN — not merely "not yet fetched" — and a ref remembers
  // that fact so later flag flips for the SAME selection no longer count as pending. The key is the
  // (address, chain, token) triple because that is exactly what `sourceBalance` is looked up by in the
  // parent (`BridgePage.tsx` — `selectedSourceToken` branch); changing any of the three is a genuinely
  // new "first load" and must be allowed to show the row again.
  //
  // GATE-4 fix — "completed" must be an OBSERVED `true → false` transition of the flag, never merely
  // "the flag happens to be false right now". `isBalancesLoading` starts its life as `useState(false)`
  // (`BridgePage.tsx`) and the fetch only raises it from a post-commit `useEffect` — so the very FIRST
  // render after an address lands (autoconnect, or a Solana/Tron manual connect, neither of which hits
  // the pre-address flag writers) sees exactly (balance null, flag false). That is "no load has
  // STARTED", not "a load completed and found nothing", and the two are indistinguishable from the
  // flag's instantaneous value alone. A second ref remembers the PREVIOUS flag value for this same
  // selection key so the latch can tell them apart: it may fire only on a render where the flag was
  // true last render and is false this render.
  const balanceSelectionKey = `${sourceAddress ?? ''}|${sourceChain ?? ''}|${sourceToken}`
  const establishedAbsentRef = useRef({ key: balanceSelectionKey, establishedAbsent: false })
  if (establishedAbsentRef.current.key !== balanceSelectionKey) {
    establishedAbsentRef.current = { key: balanceSelectionKey, establishedAbsent: false }
  }
  const prevLoadingRef = useRef({ key: balanceSelectionKey, wasLoading: isBalanceLoading })
  if (prevLoadingRef.current.key !== balanceSelectionKey) {
    // A new selection has no history of its own yet — seed with the CURRENT flag value so this render
    // cannot itself be mistaken for an observed transition.
    prevLoadingRef.current = { key: balanceSelectionKey, wasLoading: isBalanceLoading }
  }
  // GATE-4 fix (finding 2) — a completed load is only "established absent" if it also SUCCEEDED.
  // `lastBalanceLoadSucceeded` defaults to `true`, so a caller that never passes it keeps the ORIGINAL
  // arm-on-any-completion behaviour; a caller that does pass it (BridgePage) refuses to latch a
  // completed-but-FAILED first load, leaving the pending row available on the next retry.
  if (prevLoadingRef.current.wasLoading && !isBalanceLoading && sourceBalance === null && lastBalanceLoadSucceeded) {
    establishedAbsentRef.current.establishedAbsent = true
  }
  prevLoadingRef.current.wasLoading = isBalanceLoading
  const showBalancePendingRow = sourceBalance === null && isBalanceLoading
    && !establishedAbsentRef.current.establishedAbsent

  // #261 F7-2 — "Max" is a tracked STATE, not a one-shot fill.
  //
  // On the first Max tap there is usually no `dexFeeEstimate` yet (the user has typed nothing, so
  // nothing has been quoted), which means `maxReserve` is still 0 and Max fills the FULL balance —
  // the exact bug F7 exists to fix, plus the two-tap workaround it produces. Remembering that Max is
  // active lets the field RE-SNAP when the estimate lands.
  //
  // `lastMaxSnapRef` holds the value we last wrote, so the effect below is idempotent per computed
  // value and cannot chase its own output.
  //
  // AN EARLIER VERSION OF THIS COMMENT CLAIMED CONVERGENCE WAS STRUCTURAL — that the reserve is
  // amount-INDEPENDENT (gas = gasPrice × a fixed limit × buffer, dex.ts:1233-1241; fixFee is flat), so
  // re-snapping could not move the reserve that produced it. That argument was answering the wrong
  // question twice over, and both holes were found in review rather than here:
  //   1. It described the flag-ON reserve, while the effect was ALSO running against the legacy
  //      `opexUsd = amountUsd − receiveUsd`, which is strictly increasing in the amount. Fixed by
  //      gating the whole feature on `reserveAware`.
  //   2. Even amount-independent, the reserve is UNAVAILABLE mid-requote — the estimate query carries
  //      no `placeholderData`, so it blanks to 0 on every key change and handed this effect the full
  //      balance again. That is a genuine 2-cycle that a last-value ref cannot break.
  // Convergence is now ENFORCED, not argued: the re-snap is monotone non-increasing (see below).
  const [maxActive, setMaxActive] = useState(false)
  const lastMaxSnapRef = useRef<string | null>(null)
  /**
   * `maxActive` MIRRORED IN A REF, because the state alone is not visible soon enough.
   *
   * A balance result and an estimate result can land in the SAME React commit. The clear effect calls
   * `setMaxActive(false)`, but the re-snap effect in that same commit still closes over `maxActive ===
   * true` — a state update is not observable until the next render. Declaration order does not save
   * it, and clearing `lastMaxSnapRef` alongside it actively made things worse: a null ref disables the
   * monotone guard, so the re-snap then filled `newBalance − reserve` freely. The ref is mutated
   * synchronously, so every reader in the same commit sees the cleared session.
   */
  const maxActiveRef = useRef(false)
  /**
   * #261 F7 (pass-3 door B) — the newest `reserveUpdatedAt` this session has ALREADY acted on.
   * A HIGH-WATER MARK, not "the last one": with two crossed cache entries the timestamps alternate
   * t1,t2,t1,t2… and a last-value comparison stays satisfied forever, sustaining the cycle.
   */
  const lastActedAtRef = useRef(0)

  /** Single writer for the tracked-Max session, so state and both refs can never disagree. */
  const setMaxTracked = (on: boolean, snap: string | null) => {
    maxActiveRef.current = on
    lastMaxSnapRef.current = snap
    // Starting a session adopts the estimate the user just acted on; ending one resets the mark so
    // the next session is judged on its own terms rather than inheriting this one's high-water.
    lastActedAtRef.current = on ? reserveUpdatedAt : 0
    setMaxActive(on)
  }

  // Receive input state (mirrors send pattern)
  const [localReceiveAmount, setLocalReceiveAmount] = useState('')
  const lastDispatchedRecvRef = useRef('')

  // Debounced dispatch — skip when user is typing in receive field
  useEffect(() => {
    if (localAmount === lastDispatchedRef.current) return
    if (activeInputRef.current === 'receive') return
    const timer = setTimeout(() => {
      lastDispatchedRef.current = localAmount
      onAmountChange(localAmount)
    }, 300)
    return () => clearTimeout(timer)
  }, [localAmount, onAmountChange])

  // Sync from parent — also update lastDispatched to prevent stale send debounce
  useEffect(() => {
    if (amount !== localAmount) {
      // #261 F7 (GATE-4 F-B) — a parent-driven write that is NOT our own snap ends the Max session.
      // The live case is post-confirm: handleReviewConfirm -> executeTransfer -> reset() clears the
      // amount, and a higher-gas estimate resolving inside the ~500ms debounce then computed a LOWER
      // value than the watermark, so the monotone guard accepted it and REFILLED the just-cleared
      // field (note showing) during an in-flight transfer. No fund impact — the executed amount is
      // captured in the confirm closure — but the card contradicted the transfer it had just started.
      if (amount !== lastMaxSnapRef.current) setMaxTracked(false, null)
      setLocalAmount(amount)
      lastDispatchedRef.current = amount
    }
  }, [amount]) // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced dispatch for receive input — skip when user is typing in send field
  useEffect(() => {
    if (!receiveEditable || !onReceiveAmountChange) return
    if (localReceiveAmount === lastDispatchedRecvRef.current) return
    if (activeInputRef.current === 'send') return
    const timer = setTimeout(() => {
      lastDispatchedRecvRef.current = localReceiveAmount
      onReceiveAmountChange(localReceiveAmount)
    }, 300)
    return () => clearTimeout(timer)
  }, [localReceiveAmount, receiveEditable, onReceiveAmountChange])

  // Decimal precision allowed in the amount inputs is token-dependent: stablecoins use 2,
  // but native assets (ETH/POL/BNB) need finer precision (e.g. 0.0006 ETH). Without this the
  // 2-decimal cap would truncate a native amount to "0.00" and break native swaps + reverse quotes.
  const srcIsNative = !!TOKEN_PICKER_LIST.find(t => t.symbol === sourceToken)?.isNative
  const destIsNative = !!TOKEN_PICKER_LIST.find(t => t.symbol === destToken)?.isNative
  // When EITHER leg is a native asset (ETH/SOL/POL/BNB/TRX), allow 6 decimals on BOTH amount
  // fields — native amounts are small (e.g. 0.012345 SOL) and need the precision; pure
  // stable↔stable swaps keep the clean 2-decimal cap.
  const anyNative = srcIsNative || destIsNative
  const maxSendDecimals = anyNative ? 6 : 2
  const maxRecvDecimals = anyNative ? 6 : 2

  // Decimals the Half/Max fill floors to. 2dp is right for stables but DESTROYS a native balance:
  // floorToDecimals(0.00118 ETH, 2) === 0, so Half/Max would silently set 0 and the CTA would stay
  // "Enter Amount" while manual entry worked. Native legs get 6dp (matching the 6-decimal display the
  // rest of this component already uses for native — see srcIsNative above).
  const balanceDp = srcIsNative ? 6 : 2

  /**
   * THE single definition of "Max" — used by the Max button, the cold-start re-snap, and the
   * exact-balance blur snap, so the three can never disagree. Three surfaces computing the same
   * number three ways is how a WYSIWYG invariant (F7 I-1) rots.
   *
   * #94 Bug 1 — FLOOR, never round UP past the balance (toFixed(2) rounds to nearest, so
   * 2.926 → "2.93" > balance → "insufficient balance" revert). With no reserve, floor the balance
   * STRING to avoid a float round-trip; with a reserve, the subtraction is already a float.
   */
  const computeMaxFill = (balStr: string, reserve: number): string | null => {
    const bal = parseFloat(balStr)
    if (!Number.isFinite(bal) || bal <= 0) return null
    // FLAG OFF → the pre-F7 formula, byte-identical, including its warts. Notably it fills "0" when
    // the reserve swallows the balance. That is worse UX than the fall-through below, and it is
    // deliberately NOT fixed here: "flag OFF reproduces today's behaviour exactly" is the safety
    // claim the whole EXPOSURE decision rests on, and quietly shipping an improvement on the OFF path
    // falsifies it just as surely as shipping a regression would. The improvement is part of F7 and
    // rides the flag with everything else.
    if (!reserveAware) {
      return String(reserve > 0
        ? floorToDecimals(Math.max(0, bal - reserve), balanceDp)
        : floorToDecimals(balStr, balanceDp))
    }
    // Reserve at or above the whole balance → there is no sendable amount. Return null (leave the
    // field ALONE) rather than filling "0": a zero field makes the CTA read "Enter Amount" and
    // DESTROYS the diagnostic, leaving the user worse informed than before F7, which had them at
    // "Need ~X ETH for gas — you have Y" plus the shortfall panel. Falling through to that existing
    // flow is what I-4 requires. Reachable in the real world — e.g. 0.01 ETH on Ethereum against a
    // ~0.036 ETH padded reserve.
    if (reserve > 0 && bal <= reserve) return null
    const floored = reserve > 0
      ? floorToDecimals(Math.max(0, bal - reserve), balanceDp)
      : floorToDecimals(balStr, balanceDp)
    // The same guard one level down: flooring can still land on 0 for a sub-precision remainder.
    if (!(parseFloat(floored as unknown as string) > 0)) return null
    return String(floored)
  }

  // #261 F7-2 — COLD-START RE-SNAP. Max was tapped before a quote existed (reserve 0 → full balance);
  // when the estimate lands, `maxReserve` becomes real and the field must follow. Also keeps Max
  // honest across a later requote. Writes at most once per distinct computed value (lastMaxSnapRef),
  // so it cannot chase the field change it just caused.
  // Max is an intent about ONE asset AT ONE BALANCE. Switching the source token or chain makes a
  // remembered "all of it" meaningless, and a BALANCE change makes it stale in the dangerous
  // direction: an incoming deposit would silently re-fill a much larger amount the user never chose.
  // Clear it and let them re-tap. Clearing on manual edit is handled in the input's onChange, and on
  // receive-field edits in handleReceiveInputChange, where the intent is unambiguous either way.
  //
  // DECLARED BEFORE THE RE-SNAP DELIBERATELY. Effects run in declaration order within a commit, and
  // a balance result and an estimate result can land in the SAME React batch. With this second, the
  // re-snap saw `maxActive` still true alongside the NEW balance and filled `newBalance − reserve`
  // before the clear could run. Order is load-bearing here; the monotone guard below is the belt.
  useEffect(() => {
    setMaxTracked(false, null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceToken, sourceChain, sourceBalance])

  useEffect(() => {
    if (!reserveAware || snapSuspended) return
    if (!maxActiveRef.current || sourceBalance == null) return
    // Never write while the user is in the RECEIVE field. A reverse quote drives `amount` from the
    // receive side, which moves the reserve, which would otherwise re-snap the send field out from
    // under a reverse-quoted amount — the user asks to receive 50 and ends up set to send ~99.
    if (activeInputRef.current === 'receive') return
    const next = computeMaxFill(sourceBalance, maxReserve)
    if (next == null || next === lastMaxSnapRef.current) return
    // MONOTONE NON-INCREASING — a Max session may only ever revise the amount DOWNWARD.
    //
    // The estimate query carries no `placeholderData`, so `dexFeeEstimate` is `undefined` for the
    // duration of every refetch, which drops the parent's reserve to 0 and hands this effect the FULL
    // balance again. Without this guard the field ping-ponged `B → B−r → B → B−r` (each snap changes
    // the amount, which changes the query key, which blanks the data, which re-snaps), billing an
    // extra /estimate per flip and leaving the CTA briefly enabled over an unsendable full balance.
    // `lastMaxSnapRef` alone could not catch it: the two values genuinely alternate, so neither is
    // ever "the same as last time".
    //
    // Rejecting upward moves also covers the same-batch balance race above, and is the conservative
    // direction by construction: refusing to raise the amount can only ever strand dust, whereas
    // raising it can present something the wallet will not sign (I-3 — bias high on the reserve).
    // The cost is that a genuine gas DROP no longer widens an active Max; re-tapping Max picks it up.
    // FRESHNESS (pass-3 door B) — act at most once per genuinely NEW estimate. A re-served cache
    // entry carries its original fetch timestamp, so it cannot clear this mark twice; the blank
    // window carries 0 and never clears it at all. This alone closes the original F-1 oscillation.
    if (!(reserveUpdatedAt > lastActedAtRef.current)) return
    // ...and an UPWARD revision additionally needs the reserve to be genuinely in force, not
    // failed-closed to 0 by a gasless estimate (pass-3 door A), which would snap to the FULL balance.
    if (
      lastMaxSnapRef.current != null &&
      parseFloat(next) > parseFloat(lastMaxSnapRef.current) &&
      !reserveReady
    ) return
    lastActedAtRef.current = reserveUpdatedAt
    lastMaxSnapRef.current = next
    if (next === localAmount) return
    setLocalAmount(next)
    lastDispatchedRef.current = next
    onAmountChange(next)
    // localAmount is deliberately NOT a dep: this effect reacts to the RESERVE moving, not to the
    // field. Including it would re-run on the very write above (the loop this guard exists to
    // prevent), and the ref already makes repeat writes of the same value a no-op.
    // sourceBalance is NOT a dep either — a balance change CLEARS the tracked state (above) rather
    // than re-filling, so re-snapping on it would contradict that and could silently fill a LARGER
    // amount than the user chose when a deposit lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reserveAware, snapSuspended, maxActive, maxReserve, reserveReady, reserveUpdatedAt, balanceDp])

  const sanitizeAmount = (raw: string, maxDecimals: number): string => {
    let value = raw.replace(/[^0-9.]/g, '')
    const parts = value.split('.')
    if (parts.length > 2) value = parts[0] + '.' + parts.slice(1).join('')
    if (value.length > 1 && value[0] === '0' && value[1] !== '.') {
      value = value.replace(/^0+/, '') || '0'
    }
    const finalParts = value.split('.')
    if (finalParts.length === 2 && finalParts[1].length > maxDecimals) {
      value = finalParts[0] + '.' + finalParts[1].slice(0, maxDecimals)
    }
    return value
  }

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Typing is an explicit override of "Max" — drop the tracked state so the re-snap effect stops
    // rewriting the field under the user's cursor, and hide the reserve note (it describes a Max fill,
    // and a manually-typed amount has no reserve subtracted from it).
    if (maxActiveRef.current) setMaxTracked(false, null)
    setLocalAmount(sanitizeAmount(e.target.value, maxSendDecimals))
  }

  /**
   * #261 F7 item 3 — EXACT-BALANCE SNAP. Typing your entire balance is the same gesture as tapping
   * Max ("send it all"), so it gets the same reserve-aware treatment instead of blocking on the gas
   * gate. Matched against the FULL-PRECISION balance string, not the rounded display.
   *
   * On BLUR, never per-keystroke: mid-typing, "0.5" is a prefix of "0.5123…" for exactly as long as it
   * takes to type the next character, and snapping there would fight the user's input (a named
   * falsifier in the spec). Typed HIGHER than the balance → left alone, so the existing
   * insufficient-balance / gas flows own it. Typed LOWER → left as typed.
   */
  const handleSendBlur = () => {
    activeInputRef.current = null
    if (!reserveAware || snapSuspended) return
    if (sourceBalance == null || maxReserve <= 0) return
    const typed = parseFloat(localAmount)
    const bal = parseFloat(sourceBalance)
    if (!Number.isFinite(typed) || !Number.isFinite(bal)) return
    // THE MATCH IS AGAINST WHAT THE USER CAN SEE, not only the exact on-chain balance.
    //
    // The spec said "matched against the full-precision on-chain balance", and that is what this used
    // to do — but the balance the user can READ is truncated by `formatTokenAmount` (native: 4dp at
    // >=1, 6dp below; stables 2dp) and the input itself caps at 6dp. So for any 18-decimal balance
    // carrying more precision than that — the normal case — the number on screen is literally untypeable
    // as an exact match. Someone holding 1.23456789 ETH sees `Balance: 1.2345`, types `1.2345`, and the
    // "send it all" gesture silently did not fire; they were then told to reduce to a figure with MORE
    // decimals than the balance we had just shown them. That reads as the app contradicting itself.
    //
    // So the gesture is: anything from THE NUMBER ON SCREEN up to the true balance.
    //
    // `displayedValue` REPLICATES `formatTokenAmount`'s truncation with the same expression, so the
    // threshold IS what the card renders — including that function's own float quirk, where an exactly
    // on-grid balance can render one unit low (0.003919 displays as "0.003918"). Deriving the bound
    // any other way re-opens the bug for that class: the mathematically-correct floor would be
    // 0.003919, and the user typing the 0.003918 they can see would fail to match. Matching what is
    // RENDERED is the invariant; `formatTokenAmount` is deliberately not "fixed" here, both because
    // truncating low is safe by its own contract (never overstate a balance) and because it is not
    // flag-gated — changing it would alter flag-OFF behaviour app-wide.
    //
    // Typing the displayed string makes both sides parse to the SAME double, so the comparison is
    // exact rather than tolerance-based. A previous version tested `bal - typed >= 10**-dp`, which is
    // raw float subtraction at the boundary: `1.5 - 1.4999` computes as 0.00009999999999998899, just
    // under 1e-4, so a user holding exactly 1.5 who deliberately typed 1.4999 got snapped anyway —
    // wrong for ~a third of on-grid balances, and my own "deliberate smaller amount" test passed only
    // because its fixture carried enough precision to dodge the error. Caught at GATE 4.
    //
    // Below the displayed number is therefore a deliberate smaller amount and is left exactly as
    // typed; above the balance is untouched and still falls through to the existing insufficient flows.
    const displayDp = srcIsNative ? (bal >= 1 ? 4 : 6) : 2
    const displayedValue = Math.floor(bal * 10 ** displayDp) / 10 ** displayDp
    if (typed > bal || typed < displayedValue) return
    const next = computeMaxFill(sourceBalance, maxReserve)
    if (next == null || next === localAmount) return
    setMaxTracked(true, next)
    setLocalAmount(next)
    lastDispatchedRef.current = next
    onAmountChange(next)
  }

  const handleReceiveInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Driving the RECEIVE side is an explicit non-Max intent: the reverse quote sets the send amount
    // to hit a target receive. Leaving Max tracked here let the re-snap overwrite that reverse-quoted
    // send amount as soon as the reserve moved (and the reserve MUST move — it is derived from the
    // input). Same reasoning as the send-side clear in handleAmountChange.
    if (maxActiveRef.current) setMaxTracked(false, null)
    setLocalReceiveAmount(sanitizeAmount(e.target.value, maxRecvDecimals))
  }

  // Auto-fit send font — measure raw input text (no commas, matches v56.2)
  const [sendRef, sendFontSize] = useFitFont(localAmount || '0.00', UI.numFont)
  // Auto-fit receive font — plain number without locale commas
  const recvDisplay = estimatedReceived && parseFloat(estimatedReceived) > 0
    ? (() => {
        const v = parseFloat(estimatedReceived)
        // Show enough decimals so the fee deduction is visible (up to 6, trim trailing zeros, min 2)
        const full = v.toFixed(6)
        const trimmed = full.replace(/0+$/, '')
        const [int, frac = ''] = trimmed.split('.')
        return `${int}.${frac.padEnd(2, '0')}`
      })()
    : '0.00'

  // Sync receive from parent when not actively typing in receive field
  useEffect(() => {
    if (activeInputRef.current === 'receive') return
    const synced = recvDisplay === '0.00' ? '' : recvDisplay
    if (synced !== localReceiveAmount) {
      setLocalReceiveAmount(synced)
      lastDispatchedRecvRef.current = synced
    }
  }, [estimatedReceived]) // eslint-disable-line react-hooks/exhaustive-deps

  const recvMeasureText = receiveEditable ? (localReceiveAmount || '0.00') : recvDisplay
  const [recvRef, recvFontSize] = useFitFont(recvMeasureText, UI.numFont)

  // Send input always shows raw user input (no commas/forced decimals — matches v56.2)
  const displayAmount = localAmount

  const sourceIncompatibleName = isSourceIncompatible && sourceChain
    ? CHAIN_REGISTRY[sourceChain].name : null
  const destIncompatibleName = isDestIncompatible && destinationChain
    ? CHAIN_REGISTRY[destinationChain].name : null

  // #261 F3 — the wallet-chip brand icons. Read here (not at module scope) for the same reason
  // flags.ts uses functions: the flag tests mutate `import.meta.env` and there is no
  // `vi.resetModules()` in apps/web, so a hoisted value would freeze at first import.
  const walletBrandOn = isWalletTypeLabelEnabled()

  // CTA logic
  const noWallet = !sourceAddress && !destinationAddress
  const incompatible = isSourceIncompatible || isDestIncompatible
  const missingRecipient = isRecipientMissing
  const zeroAmount = !amount || parseFloat(amount) <= 0

  let ctaText: string
  let ctaOnClick: () => void
  let ctaDisabled: boolean

  if (isLoading) {
    ctaText = 'Processing...'; ctaOnClick = () => {}; ctaDisabled = true
  } else if (noWallet) {
    ctaText = 'Connect Wallet'; ctaOnClick = () => onConnectWallet?.(); ctaDisabled = false
  } else if (incompatible) {
    ctaText = 'Switch Wallet'; ctaOnClick = isDestIncompatible ? onDestWalletClick : () => onConnectWallet?.(); ctaDisabled = false
  } else if (missingRecipient) {
    ctaText = 'Set Recipient'; ctaOnClick = onSetRecipient; ctaDisabled = false
  } else if (zeroAmount) {
    // If the route is already known-invalid, surface that instead of generic "Enter Amount"
    ctaText = ctaLabel === 'Route Not Available' ? ctaLabel : 'Enter Amount'
    ctaOnClick = () => {}; ctaDisabled = true
  } else {
    ctaText = ctaLabel || 'Send'; ctaOnClick = onSend; ctaDisabled = !canSend
  }

  const isActive = !ctaDisabled && !isLoading

  return (
    <div style={{
      // Track the (already max-width-capped) parent's width instead of the card's intrinsic
      // content width, and clip any residual inner overflow. Without an explicit width the card
      // is a fit-content flex item whose min-content — driven by wide `nowrap` balance / USD rows —
      // could exceed a narrow mobile in-app webview (Phantom ~360-390px) and force horizontal page
      // scroll. width:100% + maxWidth:100% + border-box pins it to the container; overflow:hidden
      // contains anything still too wide. (#40 part a)
      width: '100%', maxWidth: '100%', boxSizing: 'border-box',
      background: c.card, borderRadius: 24,
      borderTop: `1px solid ${c.cb}`, borderRight: `1px solid ${c.cb}`,
      borderBottom: `1px solid ${c.cb}`, borderLeft: `4px solid ${c.st}`,
      boxShadow: c.sh, transition: 'all 0.3s', overflow: 'hidden',
    }}>
    <div style={{ padding: UI.cardPad }}>
      {/* YOU SEND */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
        <h2 style={{
          fontSize: 11, fontWeight: 600, color: c.mu,
          textTransform: 'uppercase', letterSpacing: 1.2,
          margin: 0, padding: 0, fontFamily: FONT_HEADING,
        }}>
          You send
        </h2>
        <WalletChip
          connected={!!sourceAddress}
          address={sourceAddress}
          incompatibleChainName={sourceIncompatibleName}
          // #261 F3 — gated HERE as well as at the parent memo, so the flag-OFF render is this
          // component's own pre-F3 output (`walletKind` undefined → generic glyph) rather than a
          // second implementation of it. H2: flag OFF is byte-identical to today.
          walletKind={walletBrandOn ? sourceWalletKind : null}
          // Fixer F-1 — the SEND chip is ALWAYS the connected source wallet (there is no "paste a
          // source" path), so its accessible name may always claim ownership. UNFLAGGED, like the
          // aria-label itself (ADDENDUM A5) — not gated on `walletBrandOn`.
          isOwnWallet
          onClick={onSourceWalletClick}
          disabled={isLoading}
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <TokenPill
          token={sourceToken}
          chain={sourceChain}
          onTokenClick={onSourceTokenClick ?? (() => {})}
          onChainClick={onSourceChainClick}
          disabled={isLoading}
          // #301a RE-ENTRY 3 — gates the CHAIN button only (not the token button, not the card):
          // it is the network pick that persists and cannot be recovered from.
          chainPending={isRoutesLoading}
        />
        <div ref={sendRef as React.RefObject<HTMLDivElement>} style={{ flex: 1, minWidth: 0, marginLeft: 12, fontFamily: FONT_HEADING, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          <input
            type="text"
            inputMode="decimal"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            maxLength={24}
            placeholder="0.00"
            value={displayAmount}
            onChange={handleAmountChange}
            onFocus={() => { activeInputRef.current = 'send' }}
            onBlur={handleSendBlur}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (canSend) onSend() } }}
            disabled={isLoading}
            aria-label="Amount to send"
            style={{
              background: 'transparent', border: 'none', outline: 'none',
              textAlign: 'right', width: '100%', padding: 0,
              fontSize: sendFontSize, fontWeight: 600, fontFamily: FONT_HEADING,
              color: c.ac, fontVariantNumeric: 'tabular-nums',
              marginTop: 2,
            }}
          />
          {localAmount && parseFloat(localAmount) > 0 && (
            <div style={{
              display: 'flex', justifyContent: 'flex-end',
              fontSize: UI.usdFont, color: c.mu, fontWeight: 500,
              fontFamily: FONT_BODY, fontVariantNumeric: 'tabular-nums',
              marginTop: 4,
            }}>
              &#x2248; {formatFiatAmount?.(sendAmountUsd ?? localAmount) ?? `${fiatSymbol}${formatTokenAmount(sendAmountUsd ?? localAmount, { isNative: false })}`}
            </div>
          )}
        </div>
      </div>

      {/* Balance + Half/Max */}
      {/* #301 item 3 — the row is also present WHILE the FIRST balance fetch for this selection is in
          flight, so it no longer materialises out of nowhere ~10s in. It does NOT re-appear on later
          background refreshes once this selection's absence is established — see
          `showBalancePendingRow` above for why (otherwise a token with no balance entry would flicker
          in and out every 30s). `sourceBalance` is still `string | null` inside the block, so every
          consumer of it below is guarded individually rather than by this gate. */}
      {sourceAddress && (sourceBalance !== null || showBalancePendingRow) && (
        // flexWrap lets the Half/Max buttons drop below the balance on a narrow webview; the balance
        // span shrinks (minWidth:0 + ellipsis) instead of forcing the card wider than the viewport. (#40a)
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 6 }}>
          {/* #254 — symbol-protected balance (same structure as the receive side): only the NUMBER
              may ellipsis; "Balance:", {sourceToken}, and the fiat are flexShrink:0 and always render.
              Send wasn't the reported clip (it has more room) but shares the identical structure, so
              both sides are hardened for consistency. */}
          <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4, minWidth: 0, maxWidth: '100%', fontSize: 13, color: c.mu, fontWeight: 500, fontFamily: FONT_BODY }}>
            <span style={{ flexShrink: 0 }}>Balance:</span>
            {sourceBalance !== null ? (
              <>
                <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {formatTokenAmount(sourceBalance, { isNative: !!TOKEN_PICKER_LIST.find(t => t.symbol === sourceToken)?.isNative })}
                </span>
                <span style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>{sourceToken}</span>
                {formatFiatAmount?.(sourceBalance) && (
                  <span style={{ flexShrink: 0, whiteSpace: 'nowrap', color: c.mu, fontSize: 12, opacity: fiatStale ? 0.5 : 0.8 }}>
                    ({formatFiatAmount(sourceBalance)})
                  </span>
                )}
              </>
            ) : (
              // Pending: the NUMBER slot alone becomes an em dash. The label and the symbol keep their
              // flexShrink:0 structure (#254 clip hardening).
              //
              // DELIBERATE DIVERGENCE from packet T5.3, which names three spans (label/symbol/fiat):
              // the fiat span is intentionally NOT rendered here. `formatFiatAmount` takes a
              // `string` — there is no balance yet to convert, so a fiat span would have nothing
              // truthful to show (not "$0", not stale, just absent input). Two spans, not three, is
              // correct for this branch; the three-span structure is preserved everywhere a balance
              // actually exists (the sibling branch above, and the destination side).
              <>
                <span data-testid="balance-pending" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  —
                </span>
                <span style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>{sourceToken}</span>
              </>
            )}
          </span>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            {['Half', 'Max'].map(label => (
              <button
                key={label}
                onClick={() => {
                  // Belt-and-braces for the `disabled` below: with no balance there is no fill to
                  // compute, and `parseFloat(null)` would be NaN rather than an obvious failure.
                  if (sourceBalance === null) return
                  const bal = parseFloat(sourceBalance)
                  if (isNaN(bal) || bal <= 0) return
                  // maxReserve > 0: the reserve is paid ON TOP of the send, so Max = balance − reserve
                  // is the actually-sendable amount. Which costs that reserve covers depends on the F7
                  // flag and is the parent's decision — see the `maxReserve` prop doc. Flooring (inside
                  // computeMaxFill) keeps the fill ≤ balance − reserve, never over.
                  let clean: string | null
                  if (label === 'Max') {
                    clean = computeMaxFill(sourceBalance, maxReserve)
                    // #261 F7-2 — remember the intent so the field RE-SNAPS if the reserve arrives (or
                    // changes) after this tap. Set even when the fill is unchanged: the first tap on a
                    // cold card is exactly the case where the reserve is still 0 and the value we write
                    // now is wrong until the estimate lands. Flag-gated with the effect it feeds —
                    // tracking Max under the legacy reserve is what shipped a silent field rewrite.
                    // Track ONLY when a fill actually happened. `computeMaxFill` returns null when the
                    // reserve swallows the balance — nothing was written, so there is nothing to
                    // re-snap and nothing for the note to explain. Setting the state unconditionally
                    // rendered "max accounting for network costs" beside an untouched field.
                    if (reserveAware && clean != null) setMaxTracked(true, clean)
                  } else {
                    // Half is an explicit non-Max choice — cancel any tracked Max so the re-snap effect
                    // does not immediately overwrite it.
                    setMaxTracked(false, null)
                    clean = String(floorToDecimals(bal / 2, balanceDp))
                  }
                  if (clean == null) return
                  lastDispatchedRef.current = clean
                  setLocalAmount(clean)
                  onAmountChange(clean)
                }}
                // #301 item 3 — gated on `sourceBalance === null`, NEVER on `isBalanceLoading`.
                //
                // `isBalancesLoading` in the parent has a SECOND writer (BridgePage.tsx:1703/:1711)
                // and `fetchAllBalances` early-returns before its own try/finally when no wallet is
                // connected, so the flag can wedge `true` for the rest of the session; the 30s refresh
                // timer also re-raises it every cycle. Gating Max on the flag would therefore
                // flicker-disable it on every tick and could disable it permanently. Gating on "there
                // is no balance to take a fraction of" is the fact these buttons actually need, and it
                // is exactly the condition that was previously unreachable because the whole row was
                // hidden.
                disabled={isLoading || sourceBalance === null}
                style={{
                  background: c.aB, color: c.aC, border: 'none',
                  borderRadius: 6, padding: '3px 8px',
                  fontSize: 11, fontWeight: 600, fontFamily: FONT_BODY,
                  cursor: isLoading || sourceBalance === null ? 'default' : 'pointer',
                  opacity: isLoading || sourceBalance === null ? 0.5 : 1,
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* #261 F7 item 2 — INLINE RESERVE NOTE. Answers exactly one question — "why is my max less than
          my balance?" — at the moment it is asked, and only then. Deliberately NOT a standalone ⓘ next
          to the Max button and NOT a hover/click tooltip (operator, 2026-08-02): the small leading ⓘ
          here IS the indicator, and it appears only with this note.

          Conditions are narrow on purpose. NATIVE source only — a stable source's Max is the full
          balance under F7, so there is nothing held back and the old "max accounting for bridge fee"
          copy is dropped (F7-5). `maxActive` only — a manually-typed amount has no reserve subtracted,
          so the note would be describing something that did not happen. `maxReserve > 0` — with no
          usable gas figure the reserve fails closed to 0 and Max fills the full balance, so again
          there is nothing to explain. */}
      {reserveAware && maxActive && srcIsNative && maxReserve > 0 && (
        <div style={{
          display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 4,
          marginTop: 6, fontSize: 12, color: c.mu, fontWeight: 500, fontFamily: FONT_BODY,
        }}>
          <Info size={12} aria-hidden="true" style={{ flexShrink: 0 }} />
          {/* COPY (operator, 2026-08-04). Was "max accounting for gas fee" in the spec; changed
              because the reserve is gas + the deBridge flat fixFee, and the fee panel already calls
              that same fixFee "Bridge & swap cost" (FeeSection). Naming only gas made one number
              carry two names on one card, which reads as a double-charge. "network costs" covers
              both without contradicting the panel's vocabulary. */}
          <span>max accounting for network costs</span>
        </div>
      )}

      {/* #334 FE-3 — the under-Send "Minimum ~$X on this route" line was REMOVED. The route minimum now
          lives in ONE place, the CTA (BridgePage ctaLabel → denomMin), so the two can never disagree and
          the min is never shown twice. The `minAmountHintUsd` prop is retired with it. */}

      {/* SWAP BUTTON */}
      <div style={{ display: 'flex', justifyContent: 'center', margin: UI.swapMargin }}>
        <button
          onClick={onSwapDirection}
          disabled={isLoading}
          aria-label="Swap send and receive"
          className="swap-arrow"
          style={{
            width: UI.swapSize, height: UI.swapSize,
            borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: c.aB, border: `1px solid ${c.dv}`,
            color: c.aC, cursor: isLoading ? 'default' : 'pointer',
            transition: 'transform 0.2s, background 0.2s',
            opacity: isLoading ? 0.5 : 1,
          }}
        >
          <ArrowDownUp size={UI.swapIcon} aria-hidden="true" />
        </button>
      </div>

      {/* YOU RECEIVE */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
        <h2 style={{
          fontSize: 11, fontWeight: 600, color: c.mu,
          textTransform: 'uppercase', letterSpacing: 1.2,
          margin: 0, padding: 0, fontFamily: FONT_HEADING,
        }}>
          You receive
        </h2>
        <WalletChip
          connected={!!destinationAddress}
          address={destinationAddress}
          incompatibleChainName={destIncompatibleName}
          missing={isRecipientMissing}
          // #261 F3 / A4 — already null unless the recipient IS the connected wallet. A pasted
          // recipient gets the generic glyph.
          walletKind={walletBrandOn ? destWalletKind : null}
          // Fixer F-1 — the ownership claim in the accessible name. UNFLAGGED (ADDENDUM A5, same as
          // the aria-label itself), so this is NOT gated on `walletBrandOn`: a pasted third-party
          // recipient must never be announced as "Connected wallet" regardless of flag state.
          isOwnWallet={recipientIsConnectedWallet}
          onClick={onDestWalletClick}
          disabled={isLoading}
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <TokenPill
          token={destToken}
          chain={destinationChain}
          onTokenClick={onDestTokenClick ?? (() => {})}
          onChainClick={onDestChainClick}
          disabled={isLoading}
          // Same gate on the receive leg: a dest pick persists `lastDestChain` and runs the picker's
          // static reset, both decided without the route table when it is still in flight.
          chainPending={isRoutesLoading}
        />
        <div ref={recvRef as React.RefObject<HTMLDivElement>} style={{ flex: 1, minWidth: 0, marginLeft: 12, fontFamily: FONT_HEADING, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
          {receiveEditable ? (
            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              maxLength={24}
              placeholder="0.00"
              value={localReceiveAmount}
              onChange={handleReceiveInputChange}
              onFocus={() => { activeInputRef.current = 'receive' }}
              onBlur={() => {
                activeInputRef.current = null
                // Only sync to verified amount if available; otherwise keep user's input
                // until the fee estimate arrives (sync effect handles it)
                if (recvDisplay !== '0.00') {
                  setLocalReceiveAmount(recvDisplay)
                  lastDispatchedRecvRef.current = recvDisplay
                }
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (canSend) onSend() } }}
              disabled={isLoading}
              aria-label="Amount to receive"
              style={{
                background: 'transparent', border: 'none', outline: 'none',
                textAlign: 'right', width: '100%', padding: 0,
                fontSize: recvFontSize, fontWeight: 500, fontFamily: FONT_HEADING,
                color: c.gr, fontVariantNumeric: 'tabular-nums',
                marginTop: 2,
              }}
            />
          ) : (
            <div style={{
              textAlign: 'right',
              fontSize: recvFontSize, fontWeight: 500, fontFamily: FONT_HEADING,
              color: c.gr, fontVariantNumeric: 'tabular-nums',
              opacity: isLoading ? 0.5 : 1,
              transition: 'opacity 0.2s, font-size 0.15s',
              whiteSpace: 'nowrap', marginTop: 2,
            }}>
              {/* beta bug 4 — while the estimate is (re)fetching (incl. right after Max), show a
                  spinner instead of a stale/blank figure so the recompute is visible. */}
              {isQuoteLoading
                ? <span role="status" aria-label="Fetching quote" style={{ display: 'inline-block', width: 18, height: 18, verticalAlign: 'middle', border: `2px solid ${c.gr}33`, borderTopColor: c.gr, borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
                : recvDisplay}
            </div>
          )}
          {((destinationAddress && destBalance !== null && destBalance !== undefined) || (estimatedReceived && parseFloat(estimatedReceived) > 0)) && (
            <div style={{
              // Balance + "≈ $X" are two nowrap spans; on a narrow webview they wrap onto separate
              // lines (flexWrap) and the balance shrinks (minWidth:0 + ellipsis) rather than overflowing. (#40a)
              display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center',
              fontSize: UI.usdFont, color: c.mu, fontWeight: 500,
              fontFamily: FONT_BODY, fontVariantNumeric: 'tabular-nums',
              marginTop: 4, gap: 8,
            }}>
              {destinationAddress && destBalance !== null && destBalance !== undefined && (
                // #254 — only the NUMBER may ellipsis under a narrow container; "Balance:", the
                // token symbol, and the fiat are flexShrink:0 siblings so the symbol (which asset
                // this is) is never eaten. Preserves the #40a no-overflow intent (minWidth:0 +
                // maxWidth:100% let the row shrink instead of overflowing the viewport).
                <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4, minWidth: 0, maxWidth: '100%', fontSize: 13, color: c.mu, fontWeight: 500, fontFamily: FONT_BODY }}>
                  <span style={{ flexShrink: 0 }}>Balance:</span>
                  <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {formatTokenAmount(destBalance, { isNative: !!TOKEN_PICKER_LIST.find(t => t.symbol === destToken)?.isNative })}
                  </span>
                  <span style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>{destToken}</span>
                  {formatFiatAmount?.(destBalance) && (
                    <span style={{ flexShrink: 0, whiteSpace: 'nowrap', color: c.mu, fontSize: 12, opacity: fiatStale ? 0.5 : 0.8 }}>
                      ({formatFiatAmount(destBalance)})
                    </span>
                  )}
                </span>
              )}
              {estimatedReceived && parseFloat(estimatedReceived) > 0 && (
                <span style={{ whiteSpace: 'nowrap' }}>
                  &#x2248; {formatFiatAmount?.(estimatedReceivedUsd ?? estimatedReceived) ?? `${fiatSymbol}${formatTokenAmount(estimatedReceivedUsd ?? estimatedReceived, { isNative: false })}`}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* FeeSection. `isSameChain` (#261 L2) drives the card's delivery-time span and nothing else.
          The null-guard is load-bearing, not defensive noise: both props start `null`, and a bare
          `sourceChain === destinationChain` would make "nothing picked yet" read as SAME-chain and
          quote the SHORTER 1–3 min — inverting the over-estimate policy the prop documents. The
          span is gated on `amountNum > 0` alone, NOT on having a quote, so it can render before a
          route exists. This is the same bare-equality trap `terminalPanelTitle` warns about by name
          in BridgePage. */}
      <FeeSection
        feeEstimate={feeEstimate ?? null}
        amount={amount}
        secondsRemaining={secondsRemaining}
        fiatSymbol={fiatSymbol}
        fiatRate={fiatRate}
        fiatStale={fiatStale}
        formatFiatAmount={formatFiatAmount}
        minReceivedAmount={minReceivedAmount}
        minReceivedSymbol={minReceivedSymbol}
        dexFee={dexFee}
        isSameChain={isAtomicSameChain === true}
      />

      {/* CTA BUTTON */}
      <button
        onClick={ctaOnClick}
        disabled={ctaDisabled}
        style={{
          width: '100%', marginTop: 16,
          padding: `${UI.ctaPad}px 24px`,
          borderRadius: 16, border: 'none',
          fontSize: UI.ctaFont, fontWeight: 600, fontFamily: FONT_HEADING,
          cursor: ctaDisabled ? 'not-allowed' : 'pointer',
          background: isActive ? BRAND_GRADIENT : c.dv,
          color: isActive ? '#FFFFFF' : c.mu,
          transition: 'all 0.2s',
        }}
      >
        {isLoading ? (
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <span className="spin" style={{
              width: 20, height: 20, border: '2px solid currentColor',
              borderTopColor: 'transparent', borderRadius: '50%',
              display: 'inline-block', animation: 'spin 0.8s linear infinite',
            }} />
            {ctaText}
          </span>
        ) : ctaText}
      </button>
    </div>
    </div>
  )
}
